/**
 * Immutable SceneDocument mutation helpers.
 * Every function returns a new document -- never mutates in place.
 */
import type { SceneDocument, SceneNode } from '@/lib/scene-document';
import { tightenGroups, applyConstraints } from './canvaskit-layout';

// ─── Node updates ─────────────────────────────────────────────────────────

export function updateNode(
  doc: SceneDocument,
  nodeId: string,
  patch: Partial<SceneNode>,
): SceneDocument {
  const existing = doc.nodes[nodeId];
  if (!existing) return doc;

  // When a container's dimensions change, propagate to children via constraints.
  const resizing = ('width' in patch || 'height' in patch) && existing.children?.length;
  if (resizing) {
    // Re-fit groups before measuring. A group's stored box is only as fresh as
    // the last time its children were re-derived, and oldBounds has to be the
    // box the user actually grabbed or the scale ratio below is computed
    // against a size that was never on screen. No-op for frames.
    const base = tightenGroups(doc);
    const node = base.nodes[nodeId];
    if (!node) return doc;

    const oldBounds = { x: node.x, y: node.y, width: node.width, height: node.height };
    const newBounds = {
      x: patch.x ?? node.x,
      y: patch.y ?? node.y,
      width: patch.width ?? node.width,
      height: patch.height ?? node.height,
    };
    // Apply the node patch first, then propagate constraints.
    const intermediate = {
      ...base,
      nodes: { ...base.nodes, [nodeId]: { ...node, ...patch } },
    };
    return applyConstraints(intermediate, nodeId, oldBounds, newBounds);
  }

  return {
    ...doc,
    nodes: {
      ...doc.nodes,
      [nodeId]: { ...existing, ...patch },
    },
  };
}

/**
 * Batch form of updateNode — and it must stay a fold over updateNode rather
 * than a bare merge, or a container resized as part of a multi-selection would
 * skip constraint propagation and leave its children behind, while the same
 * container resized on its own would not. Patches that carry no width/height
 * (drags, nudges, align) cost nothing extra: updateNode only propagates when a
 * container's dimensions actually change.
 */
export function updateNodes(
  doc: SceneDocument,
  patches: Map<string, Partial<SceneNode>>,
): SceneDocument {
  if (patches.size === 0) return doc;
  let next = doc;
  for (const [id, patch] of patches) {
    next = updateNode(next, id, patch);
  }
  return next;
}

// ─── Add / Remove ─────────────────────────────────────────────────────────

// Per-page-load salt. Date.now() plus a counter that resets on reload can be
// re-minted identically by a second tab that loaded in the same millisecond.
// That stayed theoretical while ids never left the tab that minted them; nodes
// now arrive from other documents via the clipboard, so the salt keeps each
// session's mints disjoint.
const nodeIdSalt = Math.random().toString(36).slice(2, 8);
let nodeCounter = 0;

export function generateNodeId(): string {
  nodeCounter += 1;
  return `node_${Date.now()}_${nodeIdSalt}_${nodeCounter}`;
}

/**
 * Resolve the artboard FRAME that root-level adds should land in: the single
 * root frame of an artboard document (the email/social "page"). Workspace
 * docs are excluded (their frames carry frameCanvasId) — frames added there
 * must stay root-level siblings.
 */
export function getDefaultAddParent(doc: SceneDocument): SceneNode | null {
  if (doc.rootChildren.length !== 1) return null;
  const root = doc.nodes[doc.rootChildren[0]];
  if (!root || root.type !== 'FRAME') return null;
  if (root.frameCanvasId) return null;
  if (root.pluginData?.['isArtboard'] !== true) return null;
  return root;
}

export function addNode(
  doc: SceneDocument,
  node: SceneNode,
  parentId?: string,
): SceneDocument {
  // No explicit parent on an artboard doc: content belongs inside the page
  // frame, not floating next to it — otherwise hugging artboards never see
  // it (no auto-grow) and it renders above the page like a loose layer.
  let effectiveParent = parentId ? doc.nodes[parentId] : null;
  let nodeToAdd = node;
  if (!parentId) {
    const artboard = getDefaultAddParent(doc);
    if (artboard && artboard.id !== node.id) {
      effectiveParent = artboard;
      nodeToAdd = {
        ...node,
        parentId: artboard.id,
        // Node coords are world/doc space when no parent was given; frame
        // children are frame-local.
        x: node.x - artboard.x,
        y: node.y - artboard.y,
      };
    }
  }

  const nextNodes = { ...doc.nodes, [nodeToAdd.id]: nodeToAdd };
  let nextRootChildren = doc.rootChildren;

  if (effectiveParent) {
    nextNodes[effectiveParent.id] = {
      ...effectiveParent,
      children: [...(effectiveParent.children ?? []), nodeToAdd.id],
    };
  } else {
    nextRootChildren = [...doc.rootChildren, nodeToAdd.id];
  }

  return { ...doc, nodes: nextNodes, rootChildren: nextRootChildren };
}

export function removeNodes(
  doc: SceneDocument,
  nodeIds: string[],
): SceneDocument {
  const toRemove = new Set<string>();

  // Collect all descendants
  function collectDescendants(id: string) {
    toRemove.add(id);
    const node = doc.nodes[id];
    if (node?.children) {
      for (const childId of node.children) {
        collectDescendants(childId);
      }
    }
  }
  for (const id of nodeIds) {
    collectDescendants(id);
  }

  const nextNodes = { ...doc.nodes };
  for (const id of toRemove) {
    delete nextNodes[id];
  }

  // Remove from rootChildren
  const nextRootChildren = doc.rootChildren.filter((id) => !toRemove.has(id));

  // Remove from parent children arrays
  for (const id of nodeIds) {
    const node = doc.nodes[id];
    if (node?.parentId && nextNodes[node.parentId]) {
      const parent = nextNodes[node.parentId];
      nextNodes[node.parentId] = {
        ...parent,
        children: (parent.children ?? []).filter((cid) => !toRemove.has(cid)),
      };
    }
  }

  return { ...doc, nodes: nextNodes, rootChildren: nextRootChildren };
}

// ─── Z-order ──────────────────────────────────────────────────────────────

export function moveZOrder(
  doc: SceneDocument,
  nodeId: string,
  direction: 'forward' | 'backward' | 'toFront' | 'toBack',
): SceneDocument {
  const node = doc.nodes[nodeId];
  if (!node) return doc;

  // Get the sibling list (rootChildren or parent's children)
  const siblingList = node.parentId
    ? [...(doc.nodes[node.parentId]?.children ?? [])]
    : [...doc.rootChildren];

  const idx = siblingList.indexOf(nodeId);
  if (idx === -1) return doc;

  switch (direction) {
    case 'forward':
      if (idx >= siblingList.length - 1) return doc;
      [siblingList[idx], siblingList[idx + 1]] = [siblingList[idx + 1], siblingList[idx]];
      break;
    case 'backward':
      if (idx <= 0) return doc;
      [siblingList[idx], siblingList[idx - 1]] = [siblingList[idx - 1], siblingList[idx]];
      break;
    case 'toFront':
      siblingList.splice(idx, 1);
      siblingList.push(nodeId);
      break;
    case 'toBack':
      siblingList.splice(idx, 1);
      siblingList.unshift(nodeId);
      break;
  }

  if (node.parentId) {
    return {
      ...doc,
      nodes: {
        ...doc.nodes,
        [node.parentId]: {
          ...doc.nodes[node.parentId],
          children: siblingList,
        },
      },
    };
  }
  return { ...doc, rootChildren: siblingList };
}

/**
 * Move a node to a specific index within its sibling list.
 * Used by DnD reordering in the layers panel.
 */
export function moveNodeToIndex(
  doc: SceneDocument,
  nodeId: string,
  newIndex: number,
): SceneDocument {
  const node = doc.nodes[nodeId];
  if (!node) return doc;

  const siblingList = node.parentId
    ? [...(doc.nodes[node.parentId]?.children ?? [])]
    : [...doc.rootChildren];

  const oldIndex = siblingList.indexOf(nodeId);
  if (oldIndex === -1) return doc;
  if (oldIndex === newIndex) return doc;

  siblingList.splice(oldIndex, 1);
  siblingList.splice(newIndex, 0, nodeId);

  if (node.parentId) {
    return {
      ...doc,
      nodes: {
        ...doc.nodes,
        [node.parentId]: {
          ...doc.nodes[node.parentId],
          children: siblingList,
        },
      },
    };
  }
  return { ...doc, rootChildren: siblingList };
}

/**
 * Move a node to a new parent (or to root) at a given index.
 * Handles removing from old parent and inserting into new parent.
 */
export function reparentNode(
  doc: SceneDocument,
  nodeId: string,
  newParentId: string | null,
  insertIndex: number,
): SceneDocument {
  const node = doc.nodes[nodeId];
  if (!node) return doc;

  // Don't reparent into itself or a descendant
  if (newParentId) {
    let cur: string | null | undefined = newParentId;
    while (cur) {
      if (cur === nodeId) return doc;
      cur = doc.nodes[cur]?.parentId;
    }
  }

  const oldParentId = node.parentId;

  // Remove from old parent
  let nextNodes = { ...doc.nodes };
  let nextRootChildren = [...doc.rootChildren];

  if (oldParentId) {
    const oldParent = nextNodes[oldParentId];
    if (oldParent?.children) {
      nextNodes = {
        ...nextNodes,
        [oldParentId]: {
          ...oldParent,
          children: oldParent.children.filter((id) => id !== nodeId),
        },
      };
    }
  } else {
    nextRootChildren = nextRootChildren.filter((id) => id !== nodeId);
  }

  // Insert into new parent
  if (newParentId) {
    const newParent = nextNodes[newParentId];
    if (newParent) {
      const children = [...(newParent.children ?? [])];
      children.splice(insertIndex, 0, nodeId);
      nextNodes = {
        ...nextNodes,
        [newParentId]: { ...newParent, children },
      };
    }
  } else {
    nextRootChildren.splice(insertIndex, 0, nodeId);
  }

  // Update the node's parentId
  nextNodes = {
    ...nextNodes,
    [nodeId]: { ...nextNodes[nodeId], parentId: newParentId },
  };

  return { ...doc, nodes: nextNodes, rootChildren: nextRootChildren };
}

// ─── Group / Ungroup ──────────────────────────────────────────────────────

export function groupNodes(
  doc: SceneDocument,
  nodeIds: string[],
): SceneDocument {
  if (nodeIds.length < 2) return doc;

  // All nodes must share the same parent
  const firstNode = doc.nodes[nodeIds[0]];
  if (!firstNode) return doc;
  const parentId = firstNode.parentId;

  // Compute bounding box
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const id of nodeIds) {
    const n = doc.nodes[id];
    if (!n) return doc;
    if (n.parentId !== parentId) return doc;
    minX = Math.min(minX, n.x);
    minY = Math.min(minY, n.y);
    maxX = Math.max(maxX, n.x + n.width);
    maxY = Math.max(maxY, n.y + n.height);
  }

  const groupId = generateNodeId();
  const group: SceneNode = {
    id: groupId,
    type: 'GROUP',
    name: 'Group',
    parentId,
    children: [...nodeIds],
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };

  const nextNodes = { ...doc.nodes, [groupId]: group };
  const nodeIdSet = new Set(nodeIds);

  // Update children: offset positions, set parentId
  for (const id of nodeIds) {
    const n = nextNodes[id];
    nextNodes[id] = {
      ...n,
      x: n.x - minX,
      y: n.y - minY,
      parentId: groupId,
    };
  }

  // Update sibling list: replace first occurrence with group, remove rest
  const siblingList = parentId
    ? [...(doc.nodes[parentId]?.children ?? [])]
    : [...doc.rootChildren];

  const firstIdx = siblingList.findIndex((id) => nodeIdSet.has(id));
  const filtered = siblingList.filter((id) => !nodeIdSet.has(id));
  filtered.splice(firstIdx, 0, groupId);

  if (parentId) {
    nextNodes[parentId] = {
      ...nextNodes[parentId],
      children: filtered,
    };
    return { ...doc, nodes: nextNodes };
  }
  return { ...doc, nodes: nextNodes, rootChildren: filtered };
}

export function ungroupNode(
  doc: SceneDocument,
  groupId: string,
): SceneDocument {
  const group = doc.nodes[groupId];
  if (!group || (group.type !== 'GROUP' && group.type !== 'FRAME')) return doc;
  if (!group.children?.length) return doc;

  const nextNodes = { ...doc.nodes };
  const parentId = group.parentId;

  // Move children out: offset positions back, set parentId to group's parent
  for (const childId of group.children) {
    const child = nextNodes[childId];
    if (child) {
      nextNodes[childId] = {
        ...child,
        x: child.x + group.x,
        y: child.y + group.y,
        parentId,
      };
    }
  }

  // Replace group with its children in sibling list
  const siblingList = parentId
    ? [...(nextNodes[parentId]?.children ?? [])]
    : [...doc.rootChildren];

  const groupIdx = siblingList.indexOf(groupId);
  if (groupIdx !== -1) {
    siblingList.splice(groupIdx, 1, ...group.children);
  }

  // Remove group node
  delete nextNodes[groupId];

  if (parentId) {
    nextNodes[parentId] = { ...nextNodes[parentId], children: siblingList };
    return { ...doc, nodes: nextNodes };
  }
  return { ...doc, nodes: nextNodes, rootChildren: siblingList };
}

// ─── Clone (deep copy with new IDs) ──────────────────────────────────────

/**
 * Deep-clone a set of nodes, assigning new IDs and preserving internal
 * parent/child relationships. Returns the new nodes and a mapping of
 * old IDs to new IDs.
 */
export function cloneNodes(
  doc: SceneDocument,
  nodeIds: string[],
  offset: { dx: number; dy: number } = { dx: 0, dy: 0 },
): { nodes: Record<string, SceneNode>; newIds: string[]; idMap: Map<string, string> } {
  const idMap = new Map<string, string>();
  const cloned: Record<string, SceneNode> = {};

  // First pass: collect all nodes + descendants and assign new IDs
  function collectIds(id: string) {
    if (idMap.has(id)) return;
    idMap.set(id, generateNodeId());
    const node = doc.nodes[id];
    if (node?.children) {
      for (const childId of node.children) {
        collectIds(childId);
      }
    }
  }

  for (const id of nodeIds) {
    collectIds(id);
  }

  // Second pass: clone with remapped IDs
  for (const [oldId, newId] of idMap) {
    const orig = doc.nodes[oldId];
    if (!orig) continue;

    const isTopLevel = nodeIds.includes(oldId);
    cloned[newId] = {
      ...structuredClone(orig),
      id: newId,
      parentId: orig.parentId && idMap.has(orig.parentId)
        ? idMap.get(orig.parentId)!
        : orig.parentId,
      children: orig.children?.map((cid) => idMap.get(cid) ?? cid),
      // Only offset top-level cloned nodes
      x: orig.x + (isTopLevel ? offset.dx : 0),
      y: orig.y + (isTopLevel ? offset.dy : 0),
    };
  }

  const newIds = nodeIds.map((id) => idMap.get(id)!);
  return { nodes: cloned, newIds, idMap };
}

/**
 * Duplicate nodes the way Figma does: each clone is inserted as a sibling
 * IMMEDIATELY AFTER its original in the original's parent (or rootChildren),
 * never appended to an entered group or the document root.
 */
export function duplicateAsSiblings(
  doc: SceneDocument,
  nodeIds: string[],
  offset: { dx: number; dy: number } = { dx: 10, dy: 10 },
): { doc: SceneDocument; newIds: string[] } {
  const { nodes, newIds, idMap } = cloneNodes(doc, nodeIds, offset);
  const nextNodes = { ...doc.nodes, ...nodes };
  const nextRootChildren = [...doc.rootChildren];

  for (const oldId of nodeIds) {
    const newId = idMap.get(oldId)!;
    const orig = doc.nodes[oldId];
    if (orig?.parentId && nextNodes[orig.parentId]) {
      const parent = nextNodes[orig.parentId];
      const children = [...(parent.children ?? [])];
      children.splice(children.indexOf(oldId) + 1, 0, newId);
      nextNodes[orig.parentId] = { ...parent, children };
    } else {
      nextRootChildren.splice(nextRootChildren.indexOf(oldId) + 1, 0, newId);
      nextNodes[newId] = { ...nextNodes[newId], parentId: null };
    }
  }

  return { doc: { ...doc, nodes: nextNodes, rootChildren: nextRootChildren }, newIds };
}

/**
 * Add pre-cloned nodes into a document, inserting top-level ones into
 * rootChildren or the appropriate parent.
 */
export function addClonedNodes(
  doc: SceneDocument,
  clonedNodes: Record<string, SceneNode>,
  topLevelIds: string[],
  parentId?: string,
): SceneDocument {
  const nextNodes = { ...doc.nodes, ...clonedNodes };
  let nextRootChildren = doc.rootChildren;

  if (parentId && nextNodes[parentId]) {
    nextNodes[parentId] = {
      ...nextNodes[parentId],
      children: [...(nextNodes[parentId].children ?? []), ...topLevelIds],
    };
  } else {
    nextRootChildren = [...doc.rootChildren, ...topLevelIds];
    // Ensure top-level cloned nodes have no parentId
    for (const id of topLevelIds) {
      if (clonedNodes[id]) {
        nextNodes[id] = { ...nextNodes[id], parentId: null };
      }
    }
  }

  return { ...doc, nodes: nextNodes, rootChildren: nextRootChildren };
}

// ─── Add template as group ────────────────────────────────────────────────

/**
 * Import a SceneDocument's nodes as a GROUP into the current document.
 * All root nodes from the incoming doc become children of a new group,
 * positioned at the given center point.
 */
export function addSceneDocumentAsGroup(
  doc: SceneDocument,
  incoming: SceneDocument,
  name: string,
  center: { x: number; y: number },
): { doc: SceneDocument; groupId: string } {
  const groupId = generateNodeId();
  const idMap = new Map<string, string>();

  // Assign new IDs to all incoming nodes
  for (const id of Object.keys(incoming.nodes)) {
    idMap.set(id, generateNodeId());
  }

  // Group position: center it at the given point
  const groupX = center.x - incoming.width / 2;
  const groupY = center.y - incoming.height / 2;

  // Clone all incoming nodes with remapped IDs
  const newNodes: Record<string, SceneNode> = {};
  for (const [oldId, newId] of idMap) {
    const orig = incoming.nodes[oldId];
    if (!orig) continue;

    const isRootChild = incoming.rootChildren.includes(oldId);
    newNodes[newId] = {
      ...structuredClone(orig),
      id: newId,
      parentId: isRootChild
        ? groupId
        : (orig.parentId && idMap.get(orig.parentId)) ?? groupId,
      children: orig.children?.map((cid) => idMap.get(cid) ?? cid),
    };
  }

  // Create the group
  const group: SceneNode = {
    id: groupId,
    type: 'GROUP',
    name,
    parentId: null,
    x: groupX,
    y: groupY,
    width: incoming.width,
    height: incoming.height,
    children: incoming.rootChildren.map((id) => idMap.get(id)!),
  };

  const nextNodes = { ...doc.nodes, ...newNodes, [groupId]: group };
  const nextRootChildren = [...doc.rootChildren, groupId];

  return {
    doc: { ...doc, nodes: nextNodes, rootChildren: nextRootChildren },
    groupId,
  };
}

// ─── Link zones ──────────────────────────────────────────────────────────

export const LINK_ZONE_COLORS = [
  '#4F46E5', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16',
  '#F97316', '#14B8A6', '#A855F7', '#E11D48',
];
const LINK_ZONE_GROUP_NAME = 'link-zones';

/** Find the link-zone group node, if it exists. */
export function getLinkZoneGroup(doc: SceneDocument): SceneNode | null {
  for (const id of doc.rootChildren) {
    const node = doc.nodes[id];
    if (!node) continue;
    if (node.type === 'LINK_ZONE_GROUP') return node;
    if (node.slotRole === 'link-zone') return node;
  }
  return null;
}

const EDGE_TOLERANCE = 2; // px tolerance for matching zone edges
const MIN_ZONE_SIZE = 20; // minimum zone dimension in px

/**
 * Find neighboring zones that share edges with the given zone.
 * Used for neighbor-aware resize: when one zone grows, its neighbor shrinks.
 */
export function findZoneNeighbors(
  zoneId: string,
  doc: SceneDocument,
): { top: SceneNode[]; bottom: SceneNode[]; left: SceneNode[]; right: SceneNode[] } {
  const result = { top: [] as SceneNode[], bottom: [] as SceneNode[], left: [] as SceneNode[], right: [] as SceneNode[] };
  const zone = doc.nodes[zoneId];
  if (!zone) return result;

  const allZones = getLinkZoneNodes(doc).filter((z) => z.id !== zoneId);

  const zTop = Math.round(zone.y);
  const zBot = Math.round(zone.y + zone.height);
  const zLeft = Math.round(zone.x);
  const zRight = Math.round(zone.x + zone.width);

  for (const other of allZones) {
    const oTop = Math.round(other.y);
    const oBot = Math.round(other.y + other.height);
    const oLeft = Math.round(other.x);
    const oRight = Math.round(other.x + other.width);

    // Horizontal overlap check (zones must share some X range to be vertical neighbors)
    const xOverlap = oLeft < zRight - EDGE_TOLERANCE && oRight > zLeft + EDGE_TOLERANCE;
    // Vertical overlap check (zones must share some Y range to be horizontal neighbors)
    const yOverlap = oTop < zBot - EDGE_TOLERANCE && oBot > zTop + EDGE_TOLERANCE;

    if (xOverlap && Math.abs(oBot - zTop) <= EDGE_TOLERANCE) result.top.push(other);
    if (xOverlap && Math.abs(oTop - zBot) <= EDGE_TOLERANCE) result.bottom.push(other);
    if (yOverlap && Math.abs(oRight - zLeft) <= EDGE_TOLERANCE) result.left.push(other);
    if (yOverlap && Math.abs(oLeft - zRight) <= EDGE_TOLERANCE) result.right.push(other);
  }

  return result;
}

/** Get the individual zone children inside the link-zone group. */
export function getLinkZoneNodes(doc: SceneDocument): SceneNode[] {
  const group = getLinkZoneGroup(doc);
  if (!group?.children) return [];
  return group.children
    .map((id) => doc.nodes[id])
    .filter((n): n is SceneNode => n != null);
}

/**
 * Find every link-zone group at the scene root. Sections imported from
 * email-image-import can author multiple groups per canvas (e.g. one for a
 * 2-up product band, another for a footer social-icon row); this lets the
 * Links sidebar surface them all instead of only the first.
 */
export function getAllLinkZoneGroups(doc: SceneDocument): SceneNode[] {
  const groups: SceneNode[] = [];
  for (const id of doc.rootChildren) {
    const node = doc.nodes[id];
    if (!node) continue;
    if (node.type === 'LINK_ZONE_GROUP' || node.slotRole === 'link-zone') {
      groups.push(node);
    }
  }
  return groups;
}

/** Get the zone children for a specific link-zone group. */
export function getLinkZoneNodesForGroup(
  doc: SceneDocument,
  groupId: string,
): SceneNode[] {
  const group = doc.nodes[groupId];
  if (!group?.children) return [];
  return group.children
    .map((id) => doc.nodes[id])
    .filter((n): n is SceneNode => n != null);
}

/**
 * Every node in the scene that carries a clickable linkUrl, EXCLUDING the
 * link-zone image-map constructs (those are surfaced separately as zone blocks).
 * Agent-authored canvas emails make elements clickable by putting linkUrl
 * directly on real nodes (logo, hero, cards, button) rather than authoring a
 * LINK_ZONE_GROUP; this lets the Links sidebar + canvas overlay surface them.
 */
export function getLinkedNodes(doc: SceneDocument): SceneNode[] {
  const linked: SceneNode[] = [];
  for (const node of Object.values(doc.nodes)) {
    if (!node.linkUrl) continue;
    if (node.type === 'LINK_ZONE' || node.type === 'LINK_ZONE_GROUP') continue;
    if (node.slotRole === 'link-zone') continue;
    // Skip zone children (parented to a link-zone group) to avoid double-listing.
    const parent = node.parentId ? doc.nodes[node.parentId] : null;
    if (parent && (parent.type === 'LINK_ZONE_GROUP' || parent.slotRole === 'link-zone')) {
      continue;
    }
    linked.push(node);
  }
  return linked;
}

/**
 * Human-readable label for a linked node: an explicit name, else a snippet of
 * its text content, else a type-based fallback. Used by both the canvas overlay
 * and the Links sidebar row so they stay in sync.
 */
export function getNodeLinkLabel(node: SceneNode): string {
  const name = node.name?.trim();
  if (name) return name;
  const text = node.text?.trim();
  if (text) return text.length > 32 ? `${text.slice(0, 32)}…` : text;
  switch (node.type) {
    case 'TEXT':
      return 'Text';
    case 'IMAGE':
      return 'Image';
    case 'FRAME':
    case 'GROUP':
      return 'Group';
    default:
      return 'Shape';
  }
}

/** Ensure a link-zone group exists, creating it if needed. Returns updated doc + group id. */
function ensureLinkZoneGroup(doc: SceneDocument): { doc: SceneDocument; groupId: string } {
  const existing = getLinkZoneGroup(doc);
  if (existing) return { doc, groupId: existing.id };

  const groupId = generateNodeId();
  const group: SceneNode = {
    id: groupId,
    type: 'GROUP',
    name: LINK_ZONE_GROUP_NAME,
    slotRole: 'link-zone',
    parentId: null,
    x: 0,
    y: 0,
    width: doc.width,
    height: doc.height,
    children: [],
  };

  return {
    doc: {
      ...doc,
      nodes: { ...doc.nodes, [groupId]: group },
      rootChildren: [...doc.rootChildren, groupId],
    },
    groupId,
  };
}

/**
 * Find the first uncovered full-width horizontal band in the canvas.
 * Scans top-to-bottom. Returns { y, height } of the free band, or null
 * if the canvas is fully covered by zones.
 */
function findFreeHorizontalBand(
  zones: SceneNode[],
  canvasHeight: number,
): { y: number; height: number } | null {
  if (zones.length === 0) return { y: 0, height: canvasHeight };

  // Collect Y boundaries from all zone edges
  const ySet = new Set<number>([0, canvasHeight]);
  for (const z of zones) {
    ySet.add(Math.round(z.y));
    ySet.add(Math.round(z.y + z.height));
  }
  const yBounds = [...ySet].sort((a, b) => a - b);

  // Scan each band for one with no overlapping zones
  for (let i = 0; i < yBounds.length - 1; i++) {
    const bandY = yBounds[i];
    const bandH = yBounds[i + 1] - bandY;
    if (bandH <= 0) continue;

    const hasOverlap = zones.some(
      (z) => Math.round(z.y) < bandY + bandH && Math.round(z.y + z.height) > bandY,
    );

    if (!hasOverlap) {
      return { y: bandY, height: bandH };
    }
  }

  return null; // fully covered
}

/**
 * Shrink all zones proportionally to free space at the bottom for a new row.
 * Returns updated nodes map and the Y position + height of the freed space.
 */
function shrinkZonesToFitNewRow(
  zones: SceneNode[],
  nodes: Record<string, SceneNode>,
  canvasHeight: number,
): { nodes: Record<string, SceneNode>; newRowY: number; newRowH: number } {
  // Find the actual extent of zones (they might not start at 0 or end at canvasHeight)
  let minY = canvasHeight;
  let maxY = 0;
  for (const z of zones) {
    minY = Math.min(minY, z.y);
    maxY = Math.max(maxY, z.y + z.height);
  }
  const currentSpan = maxY - minY;

  // New row gets a proportional share
  const newRowH = Math.max(Math.round(currentSpan / (zones.length + 1)), 20);
  const scale = (currentSpan - newRowH) / currentSpan;

  const nextNodes = { ...nodes };
  for (const z of zones) {
    const relY = z.y - minY; // position relative to start of zone block
    const newRelY = Math.round(relY * scale);
    const newRelBottom = Math.round((relY + z.height) * scale);
    nextNodes[z.id] = {
      ...z,
      y: minY + newRelY,
      height: Math.max(newRelBottom - newRelY, 1),
    };
  }

  const newRowY = minY + Math.round(currentSpan * scale);
  return { nodes: nextNodes, newRowY, newRowH };
}

/**
 * Create a zone node for placement inside a link-zone group.
 */
function makeZoneNode(
  groupId: string,
  index: number,
  x: number,
  y: number,
  width: number,
  height: number,
): SceneNode {
  const id = generateNodeId();
  return {
    id,
    type: 'RECTANGLE',
    parentId: groupId,
    name: `link-zone-${index}`,
    x,
    y,
    width,
    height,
    fills: [{ type: 'SOLID' as const, color: LINK_ZONE_COLORS[index % LINK_ZONE_COLORS.length] }],
    opacity: 0.3,
    linkUrl: '',
  };
}

/**
 * Add a single link zone.
 *
 * - No zones yet: full-canvas zone.
 * - Free space exists: fill the first available horizontal band.
 * - Fully covered: shrink existing zones to make room, place at bottom.
 */
export function addLinkZone(doc: SceneDocument): SceneDocument {
  const { doc: nextDoc, groupId } = ensureLinkZoneGroup(doc);
  const group = nextDoc.nodes[groupId];
  const existingZones = (group.children ?? [])
    .map((id) => nextDoc.nodes[id])
    .filter((n): n is SceneNode => n != null);
  const existingCount = existingZones.length;

  if (existingCount === 0) {
    const zone = makeZoneNode(groupId, 0, 0, 0, doc.width, doc.height);
    return {
      ...nextDoc,
      nodes: {
        ...nextDoc.nodes,
        [zone.id]: zone,
        [groupId]: { ...group, children: [zone.id] },
      },
    };
  }

  // Try to find free space first
  const free = findFreeHorizontalBand(existingZones, doc.height);

  if (free) {
    const zone = makeZoneNode(groupId, existingCount, 0, free.y, doc.width, free.height);
    return {
      ...nextDoc,
      nodes: {
        ...nextDoc.nodes,
        [zone.id]: zone,
        [groupId]: { ...group, children: [...(group.children ?? []), zone.id] },
      },
    };
  }

  // No free space -- shrink existing zones to make room
  const { nodes: shrunkNodes, newRowY, newRowH } = shrinkZonesToFitNewRow(
    existingZones, nextDoc.nodes, doc.height,
  );
  const zone = makeZoneNode(groupId, existingCount, 0, newRowY, doc.width, newRowH);

  return {
    ...nextDoc,
    nodes: {
      ...shrunkNodes,
      [zone.id]: zone,
      [groupId]: { ...group, children: [...(group.children ?? []), zone.id] },
    },
  };
}

/**
 * Remove a link zone and expand a neighbor to absorb its space.
 * Maintains 100% canvas coverage. If it's the last zone, removes the group.
 */
export function removeLinkZone(doc: SceneDocument, nodeId: string): SceneDocument {
  const node = doc.nodes[nodeId];
  if (!node || !node.parentId) return doc;

  const group = doc.nodes[node.parentId];
  if (!group?.children) return doc;

  const remainingChildren = group.children.filter((id) => id !== nodeId);

  // Last zone -- remove the entire group
  if (remainingChildren.length === 0) {
    const nextNodes = { ...doc.nodes };
    delete nextNodes[nodeId];
    delete nextNodes[node.parentId];
    return {
      ...doc,
      nodes: nextNodes,
      rootChildren: doc.rootChildren.filter((id) => id !== node.parentId),
    };
  }

  // Find the best neighbor to absorb this zone's space
  const neighbors = findZoneNeighbors(nodeId, doc);

  // Priority: same-row horizontal neighbor, then vertical neighbor
  let mergeTarget: SceneNode | null = null;
  let mergeAxis: 'horizontal' | 'vertical' = 'horizontal';

  // Horizontal: right neighbor first, then left
  if (neighbors.right.length > 0) {
    mergeTarget = neighbors.right[0];
    mergeAxis = 'horizontal';
  } else if (neighbors.left.length > 0) {
    mergeTarget = neighbors.left[0];
    mergeAxis = 'horizontal';
  }
  // Vertical: bottom first, then top
  if (!mergeTarget) {
    if (neighbors.bottom.length > 0) {
      mergeTarget = neighbors.bottom[0];
      mergeAxis = 'vertical';
    } else if (neighbors.top.length > 0) {
      mergeTarget = neighbors.top[0];
      mergeAxis = 'vertical';
    }
  }

  const nextNodes = { ...doc.nodes };
  delete nextNodes[nodeId];

  if (mergeTarget) {
    const mt = nextNodes[mergeTarget.id];
    if (mergeAxis === 'horizontal') {
      const newX = Math.min(node.x, mergeTarget.x);
      const newW = node.width + mergeTarget.width;
      nextNodes[mergeTarget.id] = { ...mt, x: newX, width: newW };
    } else {
      const newY = Math.min(node.y, mergeTarget.y);
      const newH = node.height + mergeTarget.height;
      nextNodes[mergeTarget.id] = { ...mt, y: newY, height: newH };
    }
  }

  nextNodes[node.parentId] = { ...group, children: remainingChildren };
  return { ...doc, nodes: nextNodes };
}

// Link zone layouts: horizontal bands and/or grid (rows x columns).
// Grid layouts produce zones that tile the canvas perfectly -- no gaps or overlaps.
export type LinkZoneLayoutId =
  | 'half'
  | 'thirds'
  | 'two-third-top'
  | 'two-third-bottom'
  | '2-col'
  | '3-col'
  | '4-col'
  | '2x2'
  | '2x3'
  | '2x4'
  | '3x2'
  | '3x3';

/**
 * Build a rows x cols grid of rects, distributing remainder pixels to the
 * last column/row so widths sum to exactly `w` and heights sum to exactly `h`.
 */
function buildGridRects(
  rows: number,
  cols: number,
  w: number,
  h: number,
): { x: number; y: number; width: number; height: number }[] {
  const rects: { x: number; y: number; width: number; height: number }[] = [];
  const baseH = Math.floor(h / rows);
  const baseW = Math.floor(w / cols);

  let curY = 0;
  for (let r = 0; r < rows; r++) {
    const rowH = r === rows - 1 ? h - curY : baseH;
    let curX = 0;
    for (let c = 0; c < cols; c++) {
      const colW = c === cols - 1 ? w - curX : baseW;
      rects.push({ x: curX, y: curY, width: colW, height: rowH });
      curX += colW;
    }
    curY += rowH;
  }

  return rects;
}

function computeLayoutRects(
  layoutId: LinkZoneLayoutId,
  w: number,
  h: number,
): { x: number; y: number; width: number; height: number }[] {
  switch (layoutId) {
    // Existing horizontal-only presets
    case 'half':
      return buildGridRects(2, 1, w, h);
    case 'thirds':
      return buildGridRects(3, 1, w, h);
    case 'two-third-top':
      return [
        { x: 0, y: 0, width: w, height: Math.round((h * 2) / 3) },
        { x: 0, y: Math.round((h * 2) / 3), width: w, height: h - Math.round((h * 2) / 3) },
      ];
    case 'two-third-bottom':
      return [
        { x: 0, y: 0, width: w, height: Math.round(h / 3) },
        { x: 0, y: Math.round(h / 3), width: w, height: h - Math.round(h / 3) },
      ];
    // Column-only presets (1 row, N columns)
    case '2-col':
      return buildGridRects(1, 2, w, h);
    case '3-col':
      return buildGridRects(1, 3, w, h);
    case '4-col':
      return buildGridRects(1, 4, w, h);
    // Grid presets (N rows x M columns)
    case '2x2':
      return buildGridRects(2, 2, w, h);
    case '2x3':
      return buildGridRects(2, 3, w, h);
    case '2x4':
      return buildGridRects(2, 4, w, h);
    case '3x2':
      return buildGridRects(3, 2, w, h);
    case '3x3':
      return buildGridRects(3, 3, w, h);
  }
}

/**
 * Append a grid of (rows x cols) zones to the existing link zone layout.
 *
 * 1. Find first free horizontal band -- place grid there, don't touch existing zones.
 * 2. If no free space -- shrink existing zones proportionally, place at the bottom.
 */
function appendLinkZoneGrid(doc: SceneDocument, rows: number, cols: number): SceneDocument {
  const group = getLinkZoneGroup(doc);
  if (!group?.children) return doc;

  const existingZones = group.children
    .map((id) => doc.nodes[id])
    .filter((n): n is SceneNode => n != null);

  if (existingZones.length === 0) return doc;

  const free = findFreeHorizontalBand(existingZones, doc.height);

  let nextNodes = { ...doc.nodes };
  let blockY: number;
  let blockH: number;

  if (free) {
    // Free space found -- use it, don't touch existing zones
    blockY = free.y;
    blockH = free.height;
  } else {
    // No free space -- shrink existing zones to make room
    const result = shrinkZonesToFitNewRow(existingZones, doc.nodes, doc.height);
    nextNodes = result.nodes;
    blockY = result.newRowY;
    blockH = result.newRowH;
  }

  // Build the grid rects within the target band
  const rects = buildGridRects(rows, cols, doc.width, blockH);

  const existingCount = existingZones.length;
  const newChildIds: string[] = [];

  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    const zone = makeZoneNode(
      group.id, existingCount + i,
      r.x, blockY + r.y, r.width, r.height,
    );
    nextNodes[zone.id] = zone;
    newChildIds.push(zone.id);
  }

  nextNodes[group.id] = {
    ...group,
    children: [...group.children, ...newChildIds],
  };

  return { ...doc, nodes: nextNodes };
}

/** Grid spec for a layout preset: how many rows and columns it adds. */
function layoutGridSpec(layoutId: LinkZoneLayoutId): { rows: number; cols: number } {
  switch (layoutId) {
    case 'half': return { rows: 2, cols: 1 };
    case 'thirds': return { rows: 3, cols: 1 };
    case 'two-third-top': return { rows: 2, cols: 1 };
    case 'two-third-bottom': return { rows: 2, cols: 1 };
    case '2-col': return { rows: 1, cols: 2 };
    case '3-col': return { rows: 1, cols: 3 };
    case '4-col': return { rows: 1, cols: 4 };
    case '2x2': return { rows: 2, cols: 2 };
    case '2x3': return { rows: 2, cols: 3 };
    case '2x4': return { rows: 2, cols: 4 };
    case '3x2': return { rows: 3, cols: 2 };
    case '3x3': return { rows: 3, cols: 3 };
  }
}

export function setLinkZoneLayout(doc: SceneDocument, layoutId: string): SceneDocument {
  const lid = layoutId as LinkZoneLayoutId;
  const existingGroup = getLinkZoneGroup(doc);
  const existingZones = existingGroup
    ? (existingGroup.children ?? []).map((id) => doc.nodes[id]).filter((n): n is SceneNode => n != null)
    : [];

  // If zones exist, append the preset's grid into available space
  if (existingZones.length > 0) {
    const spec = layoutGridSpec(lid);
    return appendLinkZoneGrid(doc, spec.rows, spec.cols);
  }

  // No zones: full replacement
  let nextDoc = doc;
  if (existingGroup) {
    nextDoc = removeNodes(doc, [existingGroup.id]);
  }

  const groupId = generateNodeId();
  const rects = computeLayoutRects(lid, doc.width, doc.height);
  const newNodes: Record<string, SceneNode> = {};
  const childIds: string[] = [];

  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    const id = generateNodeId();
    newNodes[id] = {
      id,
      type: 'RECTANGLE',
      parentId: groupId,
      name: `link-zone-${i}`,
      x: r.x,
      y: r.y,
      width: r.width,
      height: r.height,
      fills: [{ type: 'SOLID' as const, color: LINK_ZONE_COLORS[i % LINK_ZONE_COLORS.length] }],
      opacity: 0.3,
      linkUrl: '',
    };
    childIds.push(id);
  }

  const group: SceneNode = {
    id: groupId,
    type: 'GROUP',
    name: LINK_ZONE_GROUP_NAME,
    slotRole: 'link-zone',
    parentId: null,
    x: 0,
    y: 0,
    width: doc.width,
    height: doc.height,
    children: childIds,
  };

  return {
    ...nextDoc,
    nodes: { ...nextDoc.nodes, ...newNodes, [groupId]: group },
    rootChildren: [...nextDoc.rootChildren, groupId],
  };
}

// ─── Document resize ──────────────────────────────────────────────────────

export function resizeDocument(
  doc: SceneDocument,
  width: number,
  height: number,
): SceneDocument {
  return { ...doc, width, height };
}
